import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  DatabaseIcon,
  TrashIcon,
  CloudIcon,
  XIcon,
  WrenchIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

const DEV_TOOLBAR_STORAGE_KEY = 'dev-toolbar-settings';
const DATA_PROVIDER_KEY = 'dev-data-provider';
const CLEAR_DATA_KEY = 'dev-clear-data';

export type DataProviderMode = 'fake' | 'real';

interface DevToolbarSettings {
  isExpanded: boolean;
}

function getStoredSettings(): DevToolbarSettings {
  try {
    const stored = localStorage.getItem(DEV_TOOLBAR_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return { isExpanded: false };
}

function saveSettings(settings: DevToolbarSettings) {
  localStorage.setItem(DEV_TOOLBAR_STORAGE_KEY, JSON.stringify(settings));
}

export function getDataProviderMode(): DataProviderMode {
  try {
    const stored = localStorage.getItem(DATA_PROVIDER_KEY);
    if (stored === 'real' || stored === 'fake') {
      return stored;
    }
  } catch {
    // Ignore
  }
  return 'real'; // Default to real for dev environments
}

export function setDataProviderMode(mode: DataProviderMode) {
  localStorage.setItem(DATA_PROVIDER_KEY, mode);
}

export function isDataCleared(): boolean {
  return localStorage.getItem(CLEAR_DATA_KEY) === 'true';
}

export function setDataCleared(cleared: boolean) {
  if (cleared) {
    localStorage.setItem(CLEAR_DATA_KEY, 'true');
  } else {
    localStorage.removeItem(CLEAR_DATA_KEY);
  }
}

export function DevToolbar() {
  const [settings, setSettings] =
    useState<DevToolbarSettings>(getStoredSettings);
  const [dataProviderMode, setDataProviderModeState] =
    useState<DataProviderMode>(getDataProviderMode);
  const [dataCleared, setDataClearedState] = useState(isDataCleared);

  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  const handleToggleExpand = () => {
    setSettings(prev => ({ ...prev, isExpanded: !prev.isExpanded }));
  };

  const handleToggleDataProvider = () => {
    const newMode = dataProviderMode === 'fake' ? 'real' : 'fake';
    setDataProviderMode(newMode);
    setDataProviderModeState(newMode);
    // Reload to apply new data provider
    window.location.reload();
  };

  const handleToggleClearData = () => {
    const newCleared = !dataCleared;
    setDataCleared(newCleared);
    setDataClearedState(newCleared);
    // Reload to apply
    window.location.reload();
  };

  if (!settings.isExpanded) {
    return (
      <Button
        // variant="secondary"
        size="icon"
        className="fixed bottom-4 right-4 z-50 size-10 rounded-full shadow-lg bg-violet text-white hover:bg-violet/80"
        onClick={handleToggleExpand}
      >
        <WrenchIcon className="size-5" />
      </Button>
    );
  }

  return (
    <div className="dark fixed bottom-4 right-4 z-50 bg-obsidian border rounded-lg shadow-lg p-3 flex flex-col gap-3 min-w-[200px]">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-violet">Dev Toolbar</span>
        <Button
          variant="ghost"
          size="icon"
          className="size-6 text-white hover:text-white hover:bg-white/10"
          onClick={handleToggleExpand}
        >
          <XIcon className="size-4" />
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        <Button
          variant="secondary"
          size="sm"
          className={cn(
            'justify-start gap-2',
            dataProviderMode === 'fake' && 'bg-violet/20 border-violet'
          )}
          onClick={handleToggleDataProvider}
        >
          {dataProviderMode === 'fake' ? (
            <DatabaseIcon className="size-4" />
          ) : (
            <CloudIcon className="size-4" />
          )}
          <span className="flex-1 text-left">
            {dataProviderMode === 'fake' ? 'Fake Data' : 'Real API'}
          </span>
          <span className="text-[10px] text-muted-foreground">
            Click to switch
          </span>
        </Button>

        <Button
          variant="secondary"
          size="sm"
          className={cn(
            'justify-start gap-2',
            dataCleared && 'bg-ruby/20 border-ruby'
          )}
          onClick={handleToggleClearData}
        >
          <TrashIcon className="size-4" />
          <span className="flex-1 text-left">
            {dataCleared ? 'Data Cleared' : 'Clear Data'}
          </span>
          <span className="text-[10px] text-muted-foreground">
            {dataCleared ? 'Click to restore' : 'Test empty states'}
          </span>
        </Button>
      </div>

      <div className="text-[10px] text-muted-foreground border-t pt-2">
        Environment: {import.meta.env.VITE_ENVIRONMENT || 'development'}
      </div>
    </div>
  );
}

export function shouldShowDevToolbar(): boolean {
  const env = import.meta.env.VITE_ENVIRONMENT;
  if (env === 'production') return false;

  return (
    !env ||
    env === 'development' ||
    env === 'feature-01' ||
    env === 'feature-02'
  );
}
